import React, { useState, forwardRef, useCallback, useEffect } from 'react'
import { Select, Stack, Box, Text, Label } from '@sanity/ui'
import { set, unset } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'

interface VehicleValue {
  make?: {
    _ref: string
    _type: 'reference'
  }
  model?: {
    _ref: string
    _type: 'reference'
  }
  generation?: {
    _ref: string
    _type: 'reference'
  }
  submodel?: {
    _ref: string
    _type: 'reference'
  }
  search?: string // Enhanced feature - auto-generated search string
}

interface MakeOption {
  _id: string
  name: string
  models?: ModelOption[]
}

interface ModelOption {
  _id: string
  name: string
  generations?: GenerationOption[]
}

interface GenerationOption {
  _id: string
  name: string
  submodels?: SubmodelOption[]
}

interface SubmodelOption {
  _id: string
  name: string
}

interface VehicleInput2Props extends ObjectInputProps {
  value?: VehicleValue
}

const VehicleInput2 = forwardRef<HTMLDivElement, VehicleInput2Props>((props, ref) => {
  const { value, onChange, schemaType } = props
  const client = useClient()
  
  const [makeOptions, setMakeOptions] = useState<MakeOption[]>([])
  const [inputValues, setInputValues] = useState({
    make: '',
    model: '',
    generation: '',
    submodel: '',
  })

  // Derived options based on current selections
  const modelOptions = 
    !value?.make || makeOptions.length === 0
      ? []
      : makeOptions.find((make) => make._id === value.make._ref)?.models || []

  const generationOptions =
    !value?.model || modelOptions.length === 0
      ? []
      : modelOptions.find((model) => model._id === value.model._ref)?.generations || []

  const submodelOptions =
    !value?.generation || generationOptions.length === 0
      ? []
      : generationOptions.find((gen) => gen._id === value.generation._ref)?.submodels || []

  // Enhanced handleChange with search string generation
  const handleChange = useCallback(
    (newValue: VehicleValue | false) => {
      if (!newValue) {
        onChange(unset())
        return
      }

      const {
        make = false,
        model = false,
        generation = false,
        submodel = false,
      } = newValue

      // Generate search string from selected values
      const withSearch: VehicleValue = {
        ...newValue,
        search: [make, model, generation, submodel]
          .map(ref => ref ? ref._ref : false)
          .filter(Boolean)
          .map(refId => {
            // Find the name for each reference ID
            const allOptions = [
              ...makeOptions,
              ...modelOptions,
              ...generationOptions,
              ...submodelOptions,
            ]
            return allOptions.find(option => option._id === refId)?.name
          })
          .filter(name => name && name !== '' && name !== null && name !== undefined)
          .join(' ')
      }

      onChange(set(withSearch))
    },
    [onChange, makeOptions, modelOptions, generationOptions, submodelOptions]
  )

  const handleMakeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.make?._ref === inputValue) return

      setInputValues({
        make: inputValue,
        model: '',
        generation: '',
        submodel: '',
      })
      
      handleChange({
        make: {
          _ref: inputValue,
          _type: 'reference',
        },
      })
    },
    [handleChange, value]
  )

  const handleModelChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.model?._ref === inputValue) return

      setInputValues(prev => ({
        ...prev,
        model: inputValue,
        generation: '',
        submodel: '',
      }))

      handleChange({
        make: value?.make,
        model: {
          _ref: inputValue,
          _type: 'reference',
        },
      })
    },
    [handleChange, value]
  )

  const handleGenerationChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.generation?._ref === inputValue) return

      setInputValues(prev => ({
        ...prev,
        generation: inputValue,
        submodel: '',
      }))

      handleChange({
        make: value?.make,
        model: value?.model,
        generation: {
          _ref: inputValue,
          _type: 'reference',
        },
      })
    },
    [handleChange, value]
  )

  const handleSubmodelChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const inputValue = event.currentTarget.value
      if (value?.submodel?._ref === inputValue) return

      setInputValues(prev => ({
        ...prev,
        submodel: inputValue,
      }))

      handleChange({
        make: value?.make,
        model: value?.model,
        generation: value?.generation,
        submodel: {
          _ref: inputValue,
          _type: 'reference',
        },
      })
    },
    [handleChange, value]
  )

  // Fetch vehicle hierarchy data
  useEffect(() => {
    client
      .fetch(
        `*[_type == "make" && !(_id in path("drafts.**")) && isHidden != true]{
          _id,
          name,
          models[!@->.isHidden]->{
            _id,
            name,
            generations[]->{
              _id,
              name,
              submodels[]->{
                _id,
                name,
                hasSubmodels => {
                  submodels[]->{
                    _id,
                    name
                  }
                }
              }
            }
          }
        }`
      )
      .then((data: MakeOption[]) => {
        // Set current values based on existing selections
        let currentMakeOption: MakeOption | undefined
        let currentModelOption: ModelOption | undefined
        let currentGenerationOption: GenerationOption | undefined
        let currentSubmodelOption: SubmodelOption | undefined

        if (value?.make?._ref) {
          currentMakeOption = data.find(make => make._id === value.make!._ref)

          if (currentMakeOption && value?.model?._ref) {
            currentModelOption = currentMakeOption.models?.find(
              model => model._id === value.model!._ref
            )

            if (currentModelOption && value?.generation?._ref) {
              currentGenerationOption = currentModelOption.generations?.find(
                gen => gen._id === value.generation!._ref
              )

              if (currentGenerationOption && value?.submodel?._ref) {
                currentSubmodelOption = currentGenerationOption.submodels?.find(
                  submodel => submodel._id === value.submodel!._ref
                )
              }
            }
          }
        }

        setInputValues({
          make: currentMakeOption?._id || '',
          model: currentModelOption?._id || '',
          generation: currentGenerationOption?._id || '',
          submodel: currentSubmodelOption?._id || '',
        })

        const sortedData = data.sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
        
        setMakeOptions(sortedData)
      })
      .catch((error) => {
        console.error('Error fetching vehicle data:', error)
      })
  }, [client, value])

  return (
    <Stack gap={3} ref={ref}>
      <Box>
        <Label size={1} weight="medium">
          {schemaType.title || 'Vehicle Selection'}
        </Label>
        {schemaType.description && (
          <Text size={1} muted style={{ marginTop: 4 }}>
            {schemaType.description}
          </Text>
        )}
      </Box>
      
      <Box
        paddingX={3}
        style={{ borderLeft: '1px solid var(--card-border-color)' }}
      >
        <Stack gap={3}>
          {/* Make Selection */}
          <Box>
            <Label size={1}>Make</Label>
            <Select
              value={inputValues.make}
              onChange={handleMakeChange}
            >
              <option value="">Select Make</option>
              {makeOptions.map((make) => (
                <option key={make._id} value={make._id}>
                  {make.name}
                </option>
              ))}
            </Select>
          </Box>

          {/* Model Selection */}
          {modelOptions.length > 0 && (
            <Box>
              <Label size={1}>Model</Label>
              <Select
                value={inputValues.model}
                onChange={handleModelChange}
              >
                <option value="">Select Model</option>
                {modelOptions.map((model) => (
                  <option key={model._id} value={model._id}>
                    {model.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}

          {/* Generation Selection */}
          {generationOptions.length > 0 && (
            <Box>
              <Label size={1}>Generation</Label>
              <Select
                value={inputValues.generation}
                onChange={handleGenerationChange}
              >
                <option value="">Select Generation</option>
                {generationOptions.map((generation) => (
                  <option key={generation._id} value={generation._id}>
                    {generation.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}

          {/* Submodel Selection */}
          {submodelOptions.length > 0 && (
            <Box>
              <Label size={1}>Submodel</Label>
              <Select
                value={inputValues.submodel}
                onChange={handleSubmodelChange}
              >
                <option value="">Select Submodel</option>
                {submodelOptions.map((submodel) => (
                  <option key={submodel._id} value={submodel._id}>
                    {submodel.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}

          {/* Show generated search string for debugging */}
          {value?.search && (
            <Box paddingTop={2}>
              <Text size={1} muted>
                Search: {value.search}
              </Text>
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  )
})

VehicleInput2.displayName = 'VehicleInput2'

export default VehicleInput2